import { useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export interface TopStreamItem {
  id: number;
  location: string;
  name: string;
  muzukuru: number;
  youtube: number;
  facebook: number;
  church: number;
}

interface TopStreamsTableProps {
  rows: TopStreamItem[];
  onRowClick: (row: TopStreamItem) => void;
}

const ROWS_PER_PAGE = 5;

export default function TopStreamsTable({
  rows,
  onRowClick,
}: TopStreamsTableProps) {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const startIndex = (page - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedRows = rows.slice(startIndex, endIndex);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card sx={{ borderRadius: 3, boxShadow: 0 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Top Performing Streams
          </Typography>
          {rows.length === 0 ? (
            <Box
              sx={{
                py: 8,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">No top performing streams</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table
                  sx={{ borderCollapse: "separate", borderSpacing: "0 2px" }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F0F4F8" }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#666",
                          border: 0,
                          borderRadius: "8px 0 0 8px",
                          width: 48,
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "#666", border: 0 }}
                      >
                        Location
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "#666", border: 0 }}
                      >
                        Stream Name
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "#666", border: 0 }}
                      >
                        Muzukuru
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "#666", border: 0 }}
                      >
                        Youtube
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "#666", border: 0 }}
                      >
                        Facebook
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#666",
                          border: 0,
                          borderRadius: "0 8px 8px 0",
                        }}
                      >
                        Church
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.map((row, index) => (
                      <TableRow
                        key={row.id}
                        component={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ x: 4, transition: { duration: 0.2 } }}
                        onClick={() => onRowClick(row)}
                        sx={{
                          cursor: "pointer",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "& td": { borderBottom: "1px solid #F0F0F0" },
                          "&:hover": { backgroundColor: "#F9FAFB" },
                        }}
                      >
                        <TableCell sx={{ color: "text.secondary" }}>
                          {startIndex + index + 1}
                        </TableCell>
                        <TableCell>{row.location}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.name}
                        </TableCell>
                        <TableCell>{row.muzukuru}</TableCell>
                        <TableCell>{row.youtube}</TableCell>
                        <TableCell>{row.facebook}</TableCell>
                        <TableCell>{row.church}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 2,
                  gap: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {startIndex + 1}-{Math.min(endIndex, rows.length)} of{" "}
                  {rows.length}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  sx={{ color: "#666" }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  sx={{ color: "#666" }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
